<template>
  <div class="listing-card">
    <div class="listing-detail">
      <router-link :to="editRoute(applicant.id)" class="applicant-image">
        <AuthImage v-if="applicant.imageID" :src="applicant.imageID" />
      </router-link>
      <div class="listing-data">
        <h3>
          <router-link :to="editRoute(applicant.id)">
            <span
              :class="applicant.readAt ? 'text-success' : 'text-danger'"
              :title="readAt(applicant)"
            >
              <feather-icon
                icon="eye"
                svg-classes="mr-2 hover:text-primary cursor-pointer"
              />
            </span>
            {{ applicant.firstName }} {{ applicant.lastName }}
            {{ getAge(applicant.birthday) }}
            <span class="applicant-number">({{ applicant.number }})</span>
          </router-link>
        </h3>
        <div class="listing-data-block">
          <ul>
            <li>
              <span><i class="fal fa-map-marker-alt"></i></span>
              <template
                v-if="applicant.contactData && applicant.contactData.address"
                ><router-link :to="editRoute(applicant.id)">
                  {{ applicant.contactData.address.city }}</router-link
                >
              </template>
              <template v-else>Kein Wohnort</template>
            </li>
            <li>
              <span><i class="far fa-swords-laser"></i></span
              ><template
                v-if="
                  applicant.defaultProfile &&
                    applicant.defaultProfile.wantedOccupation
                "
                ><router-link :to="editProfileRoute(applicant.id)">{{
                  applicant.defaultProfile.wantedOccupation
                }}</router-link></template
              >
              <template v-else>Kein Wunschberuf</template>
            </li>
            <li>
              <span><i class="fas fa-calendar-check"></i></span>Beworben:
              {{ $d(Date.parse(applicant.createdAt), "short") }}
            </li>
          </ul>
        </div>
        <div class="listing-data-block">
          <ul>
            <li>
              <i class="fas fa-layer-group"></i>Gruppe:
              <template v-if="applicant.group">
                <router-link :to="editInternalRoute(applicant.id)">{{
                  applicant.group.name
                }}</router-link></template
              >
              <template v-else>Keine Gruppe</template>
            </li>
            <li>
              <i class="fas fa-address-book"></i>Letzter Kontakt:
              <template v-if="applicant.latestHistoryAction">
                <template v-if="applicant.latestHistoryAction.createdAt">
                  <router-link :to="editHistoryRoute(applicant.id)">
                    {{
                      $d(
                        Date.parse(applicant.latestHistoryAction.createdAt),
                        "short"
                      )
                    }}
                  </router-link>
                </template>
              </template>
              <template v-else>-</template>
            </li>
            <li v-if="applicant.defaultProfile">
              <i class="fas fa-traffic-light"></i>Status:
              <template v-if="applicant.defaultProfile.state"
                ><router-link :to="editInternalRoute(applicant.id)">{{
                  applicant.defaultProfile.state.name
                }}</router-link></template
              >
              <template v-else>Keine Angabe</template>
            </li>
          </ul>
        </div>
        <div class="listing-data-block listing-data-block--contact">
          <ul>
            <li>
              <i class="fas fa-phone-volume"></i>
              <template v-if="applicant.contactData.telephone"
                ><a :href="'callto:' + applicant.contactData.telephone">{{
                  applicant.contactData.telephone
                }}</a></template
              >
              <template v-else-if="applicant.contactData.telephoneMobile"
                ><a :href="'callto:' + applicant.contactData.telephoneMobile">{{
                  applicant.contactData.telephoneMobile
                }}</a></template
              >
              <template v-else>Keine Angabe</template>
            </li>
            <li>
              <i class="fas fa-envelope"></i>
              <template v-if="applicant.contactData.email">
                <router-link :to="mailerRoute(applicant.id)">{{
                  applicant.contactData.email
                }}</router-link>
              </template>
              <template v-else>Keine Angabe</template>
            </li>
            <li v-if="applicant.defaultProfile">
              <i class="fas fa-calendar-alt"></i>Verfügbar ab:
              <template v-if="applicant.defaultProfile.availibiltyCustom"
                ><router-link :to="editProfileRoute(applicant.id)">{{
                  applicant.defaultProfile.availibiltyCustom
                }}</router-link></template
              >
              <template v-else-if="applicant.defaultProfile.availibilty"
                ><router-link :to="editProfileRoute(applicant.id)">{{
                  applicant.defaultProfile.availibilty.name
                }}</router-link></template
              >
              <template v-else>Keine Angabe</template>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="listing-actions">
      <span
        class="short-profile-action"
        title="Kurzprofil"
        @click="openPreview(applicant.id)"
      >
        <i class="fal fa-user-astronaut"></i>
      </span>
      <div
        v-if="
          applicant.defaultProfile &&
            applicant.defaultProfile.internalConsultant &&
            applicant.defaultProfile.internalConsultant.firstName &&
            applicant.defaultProfile.internalConsultant.lastName
        "
        :style="
          getColorByUser(
            applicant.defaultProfile.internalConsultant.firstName,
            applicant.defaultProfile.internalConsultant.lastName
          )
        "
      >
        <span
          :title="
            `${applicant.defaultProfile.internalConsultant.firstName} ${applicant.defaultProfile.internalConsultant.lastName}`
          "
          class="shortHandCircul"
          >{{ getShorthandSymbol(applicant) }}
        </span>
      </div>
      <div v-else style="background-color:grey;color:white;">
        <span>-</span>
      </div>
      <div
        class="listing-delete cursor-pointer"
        @click="deleteRecord(applicant.id)"
      >
        <feather-icon icon="trash" svg-classes="h-5 w-5 hover:text-danger" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import AuthImage from "@/components/ui/AuthImage";

export default {
  components: {
    AuthImage
  },
  props: {
    applicant: { type: Object, required: true }
  },
  methods: {
    getShorthandSymbol(applicant) {
      if (applicant.defaultProfile.internalConsultant.shorthandSymbol) {
        return applicant.defaultProfile.internalConsultant.shorthandSymbol;
      }

      return (
        applicant.defaultProfile.internalConsultant.firstName.charAt(0) +
        applicant.defaultProfile.internalConsultant.lastName.charAt(0)
      );
    },
    getColorByUser(firstName, lastName) {
      let rgb = {
        r: this.alphabetPosition(firstName.charAt(0)),
        g: this.alphabetPosition(lastName.charAt(0)),
        b: null
      };

      rgb.b = Math.abs(rgb.r - rgb.g);

      return "background-color:rgb(" + rgb.r + "," + rgb.g + "," + rgb.b + ")";
    },
    alphabetPosition(text) {
      let result = "";
      for (let i = 0; i < text.length; i++) {
        const code = text.toUpperCase().charCodeAt(i);
        if (code > 64 && code < 91) result += `${code - 64} `;
      }

      return result.slice(0, result.length - 1) * 10;
    },
    readAt(applicant) {
      if (!applicant || !applicant.readAt) {
        return null;
      }

      const date = this.$d(Date.parse(applicant.readAt), "long");
      const consultant = applicant.readBy.consultant || {};
      return `Gesehen am ${date} von ${consultant.firstName} ${consultant.lastName}`;
    },
    getAge(birthday) {
      if (birthday) {
        var age = null;
        age = moment().diff(birthday, "years", false);
        if (age == null) {
          return "";
        } else {
          return `(${age})`;
        }
      }
    },
    async downloadImage(file) {
      const { data } = await this.$axiosStorageApi({
        url: `/file/${file}/download_auth`,
        method: "GET",
        responseType: "blob"
      });

      let reader = new FileReader();
      reader.readAsDataURL(data);
      reader.onloadend = () => {
        return reader.result;
      };
    },
    editRoute(id) {
      return `/applicant/${id}/show`;
    },
    editInternalRoute(id) {
      return `/applicant/${id}/show/profile`;
    },
    editProfileRoute(id) {
      return `/applicant/${id}/show/profile`;
    },
    editHistoryRoute(id) {
      return `/applicant/${id}/show/history`;
    },
    mailerRoute(id) {
      return `/applicant/${id}/mailer`;
    },
    openPreview() {
      this.$emit("openPreview");
    },
    deleteRecord() {
      this.$emit("delete");
    }
  }
};
</script>
